import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import DefaultDataTable from '../../components/default/DefaultDataTable';
import DefaultCard from '../../components/default/DefaultCard';
import ButtonTableAction from '../../components/buttons/ButtonTableAction';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import PageHeader from '../../components/PageHeader/PageHeader';
import SelectField from '../../components/SelectField/SelectField';
import InputField from '../../components/InputField';
import useSimilarityAlertService from '../../app/services/hooks/useSimilarityAlertService';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import '../../styles/page-components.css';

interface SimilarityAlert {
    id: number;
    brand_registration_id: number;
    brand_registration_name: string;
    publication_date: string;
    edition_number: string;
    similar_brand_name: string;
    solicitud_number: string;
    categoria: string;
    pdf_url: string;
    cve: string;
    similarity_score: number;
    similarity_percentage: number;
    match_type: string;
    match_type_display: string;
    status: string;
    status_display: string;
    reviewed_at: string | null;
    reviewed_by: string | null;
    notes: string;
    created_at: string;
}

interface Filters {
    page: number;
    per_page: number;
    search: string;
    status: string;
    brand_registration_id?: number;
}

const SimilarityAlerts = () => {
    const history = useHistory();
    const { requestConfirmation, successAlert } = useReactConfirmAlert();
    
    const { 
        getAlerts, 
        markAlertReviewed, 
        fetchingGetAlerts, 
        fetchingMarkReviewed 
    } = useSimilarityAlertService();

    const [alerts, setAlerts] = useState<SimilarityAlert[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);

    const [filter, setFilter] = useState<Filters>({
        page: 1,
        per_page: 10,
        search: '',
        status: '',
        // Desde las etapas de una marca: solo sus alertas
        brand_registration_id: Number(new URLSearchParams(window.location.search).get('brand_registration_id')) || undefined
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page]);

    const reloadTable = () => {
        getAlerts(filter, {
            onSuccess: (response: ServiceResponse) => {
                setAlerts(response.data.alerts || []);
                setTotalRows(response.data.pagination?.total || 0);
                setPendingCount(response.data.pending_count || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al cargar las alertas');
            }
        });
    };

    const handleSearch = () => {
        setFilter({ ...filter, page: 1 });
        reloadTable();
    };

    const handleMarkReviewed = (alert: SimilarityAlert, status: 'reviewed' | 'dismissed' | 'action_required') => {
        const statusLabels: Record<string, string> = {
            'reviewed': 'revisada',
            'dismissed': 'descartada',
            'action_required': 'requiere acción'
        };

        requestConfirmation({
            title: `¿Marcar como ${statusLabels[status]}?`,
            message: `La alerta de "${alert.similar_brand_name}" será marcada como ${statusLabels[status]}.`,
            buttons: {
                confirmButton: {
                    label: 'Sí, continuar',
                    onClick: () => {
                        markAlertReviewed(alert.id, { status }, {
                            onSuccess: (response: ServiceResponse) => {
                                successAlert({
                                    title: 'Éxito',
                                    message: response.message || `Alerta marcada como ${statusLabels[status]}`
                                });
                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                toast.error(response.message || 'Error al actualizar la alerta');
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar'
                }
            }
        });
    };

    // Insignias suaves, en la misma línea que el resto de las tablas
    const pill = (bg: string, color: string, label: string) => (
        <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', backgroundColor: bg, color, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {label}
        </span>
    );

    const getStatusBadge = (status: string) => {
        const statusConfig: Record<string, [string, string, string]> = {
            'pending': ['#FFF6E0', '#8A6100', 'Pendiente'],
            'reviewed': ['#E7F6EF', '#1E7B4F', 'Revisada'],
            'dismissed': ['#EEF0F4', '#5B6478', 'Descartada'],
            'action_required': ['#FDECEC', '#B42318', 'Requiere acción']
        };
        const [bg, color, label] = statusConfig[status] || statusConfig['pending'];
        return pill(bg, color, label);
    };

    const getSimilarityBadge = (percentage: number, matchType: string) => {
        if (matchType === 'contains') return pill('#FDECEC', '#B42318', 'Exacta');
        if (percentage >= 70) return pill('#FFF1E6', '#B54708', `${percentage}%`);
        return pill('#EEF2FB', '#001849', `${percentage}%`);
    };

    const columns = [
        {
            name: 'Fecha',
            selector: (row: SimilarityAlert) => row.publication_date,
            sortable: true,
            width: '110px'
        },
        {
            name: 'Marca Monitoreada',
            selector: (row: SimilarityAlert) => row.brand_registration_name,
            sortable: true,
            cell: (row: SimilarityAlert) => (
                <div style={{ fontWeight: 500 }}>
                    {row.brand_registration_name}
                </div>
            )
        },
        {
            name: 'Marca Similar Detectada',
            selector: (row: SimilarityAlert) => row.similar_brand_name,
            sortable: true,
            cell: (row: SimilarityAlert) => (
                <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary, #001849)' }}>
                        {row.similar_brand_name}
                    </div>
                    {row.solicitud_number && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            Solicitud: {row.solicitud_number}
                        </div>
                    )}
                </div>
            )
        },
        {
            name: 'Similitud',
            width: '100px',
            cell: (row: SimilarityAlert) => getSimilarityBadge(row.similarity_percentage, row.match_type)
        },
        {
            name: 'Estado',
            width: '130px',
            cell: (row: SimilarityAlert) => getStatusBadge(row.status)
        },
        {
            name: 'Acciones',
            width: '180px',
            cell: (row: SimilarityAlert) => (
                <div className="d-flex align-items-center gap-1">
                    {row.pdf_url && (
                        <ButtonTableAction
                            callbackFunction={() => window.open(row.pdf_url, '_blank')}
                            title="Ver PDF"
                            classIcon="fa-file-pdf"
                            colorIcon="text-danger"
                            sizeIcon="16"
                        />
                    )}
                    {row.status === 'pending' && (
                        <>
                            <ButtonTableAction
                                callbackFunction={() => handleMarkReviewed(row, 'reviewed')}
                                title="Marcar revisada"
                                classIcon="fa-check-circle"
                                colorIcon="text-success"
                                sizeIcon="16"
                            />
                            <ButtonTableAction
                                callbackFunction={() => handleMarkReviewed(row, 'dismissed')}
                                title="Descartar"
                                classIcon="fa-times-circle"
                                colorIcon="text-secondary"
                                sizeIcon="16"
                            />
                            <ButtonTableAction
                                callbackFunction={() => handleMarkReviewed(row, 'action_required')}
                                title="Requiere acción"
                                classIcon="fa-exclamation-triangle"
                                colorIcon="text-warning"
                                sizeIcon="16"
                            />
                        </>
                    )}
                    <ButtonTableAction
                        callbackFunction={() => history.push(`/intranet/brand-registration/stages/${row.brand_registration_id}`)}
                        title="Ver registro"
                        classIcon="fa-eye"
                        colorIcon="text-info"
                        sizeIcon="16"
                    />
                </div>
            )
        }
    ];

    return (
        <div className="container-fluid">
            <div className="page-container">
                {/* Header con breadcrumbs */}
                <PageHeader
                    title=""
                    breadcrumbs={[
                        { label: 'Inicio', path: '/intranet' },
                        { label: 'Registro de marca', path: '/intranet/brand-registration' },
                        { label: 'Alertas de Similitud', isCurrent: true }
                    ]}
                />

                <DefaultCard>
                    {/* Header con título y botón alineados */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h1 className="page-title">Alertas de Similitud</h1>
                            <p className="page-description">
                                Monitoreo de marcas similares detectadas en el Diario Oficial.{' '}
                                {pendingCount > 0 && (
                                    <span style={{ 
                                        backgroundColor: '#fff3cd', 
                                        padding: '2px 8px', 
                                        borderRadius: '12px',
                                        fontSize: '14px',
                                        fontWeight: 500 
                                    }}>
                                        {pendingCount} pendientes
                                    </span>
                                )}
                            </p>
                        </div>
                        <ButtonGeneral
                            onClick={() => history.push('/intranet/brand-registration')}
                            title="Volver a Registros"
                            variant="outline"
                            size="small"
                            icon="fa-arrow-left"
                            iconPosition="left"
                        />
                    </div>

                    {/* Filtros */}
                    <div className="row">
                        <div className="row">
                            <div className="col-md-auto">
                                <InputField
                                    type="text"
                                    label="Buscar"
                                    value={filter.search}
                                    onChange={(value) => setFilter({ ...filter, search: value })}
                                    placeholder="Buscar por nombre de marca..."
                                    size="small"
                                />
                            </div>
                            <div className="col-md-auto">
                                <SelectField
                                    value={filter.status}
                                    onChange={(value) => setFilter({ ...filter, status: value as string })}
                                    size="small"
                                >
                                    <option value="">Todos los estados</option>
                                    <option value="pending">Pendientes</option>
                                    <option value="reviewed">Revisadas</option>
                                    <option value="dismissed">Descartadas</option>
                                    <option value="action_required">Requieren acción</option>
                                </SelectField>
                            </div>
                            <div className="col-md-auto">
                                <div>
                                    <ButtonGeneral
                                        onClick={handleSearch}
                                        title="Buscar"
                                        variant="outline"
                                        size="small"
                                        icon="fa-search"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabla */}
                    <DefaultDataTable
                        columns={columns}
                        data={alerts}
                        paginationTotalRows={totalRows}
                        filter={filter}
                        setFilter={setFilter}
                        pagination={true}
                        progressPending={fetchingGetAlerts}
                    />
                </DefaultCard>
            </div>
        </div>
    );
};

export default SimilarityAlerts;

